import styled from '@emotion/styled';
import { useState } from 'react';
import { mediaQuery } from '@styles/index';
import {
  UpscTopicMainsQuestion,
  UpscTopicMcq
} from './upscCurrentAffairsTopicTypes';

const MCQ_OPTION_LABELS = ['A', 'B', 'C', 'D'];

const McqWrap = styled.div`
  padding: 15px 16px;
  border-radius: 10px;
  background: var(--color-primary-focus);
  color: var(--color-i-white);
`;

const McqBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--color-i-gold-1);
`;

const McqQuestion = styled.div`
  margin-bottom: 12px;
  font-size: 13.5px;
  line-height: 1.75;
  white-space: pre-line;
`;

const McqOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;

  ${mediaQuery.uptoMobile} {
    grid-template-columns: 1fr;
  }
`;

const McqOption = styled.button<{ $state: 'default' | 'correct' | 'wrong' }>`
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  padding: 9px 12px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 7px;
  font-family: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.08);
  color: var(--color-i-white);
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }

  ${({ $state }) =>
    $state === 'correct' &&
    `
    background: rgba(22, 163, 74, 0.3);
    border-color: var(--color-i-green);
  `}

  ${({ $state }) =>
    $state === 'wrong' &&
    `
    background: rgba(220, 38, 38, 0.3);
    border-color: var(--color-i-red);
  `}
`;

const McqOptionKey = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  color: var(--color-i-white);
  opacity: 0.4;
`;

const McqHint = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.14);
`;

const McqHintHeading = styled.div`
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 700;
  color: var(--color-i-gold-1);
`;

const McqHintText = styled.div`
  font-size: 12.5px;
  line-height: 1.6;
  color: var(--color-i-white);
  opacity: 0.85;
`;

const MainsQuestion = styled.div`
  margin-top: 10px;
  padding: 11px 14px;
  border: 1px solid var(--color-purple);
  border-radius: 8px;
  background: var(--color-purple);
`;

const MainsLabel = styled.div`
  margin-bottom: 5px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: var(--color-purple-dark);
`;

const MainsText = styled.div`
  font-size: 14px;
  font-style: italic;
  line-height: 1.75;
  color: var(--color-purple-dark);
`;

export interface UpscTopicCardMcqMainsProps {
  mcq: UpscTopicMcq;
  mainsQuestion: UpscTopicMainsQuestion;
}

const UpscTopicCardMcqMains = ({
  mcq,
  mainsQuestion
}: UpscTopicCardMcqMainsProps) => {
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | null>(
    null
  );
  const isInteractive = mcq.correctOptionIndex >= 0;

  const handleOptionClick = (optionIndex: number) => {
    if (!isInteractive || selectedOptionIndex !== null) {
      return;
    }

    setSelectedOptionIndex(optionIndex);
  };

  const getOptionState = (
    optionIndex: number
  ): 'default' | 'correct' | 'wrong' => {
    if (!isInteractive || selectedOptionIndex === null) {
      return 'default';
    }

    if (optionIndex === mcq.correctOptionIndex) {
      return 'correct';
    }

    if (optionIndex === selectedOptionIndex) {
      return 'wrong';
    }

    return 'default';
  };

  const showHint =
    isInteractive && selectedOptionIndex !== null && Boolean(mcq.hint);

  if (!isInteractive) {
    return mainsQuestion.subHeading && mainsQuestion.question ? (
      <MainsQuestion>
        <MainsLabel>{mainsQuestion.subHeading}</MainsLabel>
        <MainsText>{mainsQuestion.question}</MainsText>
      </MainsQuestion>
    ) : null;
  }

  return (
    <>
      <McqWrap>
        <McqBadge>{mcq.subHeading}</McqBadge>
        <McqQuestion>{mcq.question}</McqQuestion>
        <McqOptions>
          {mcq.options.map((option, optionIndex) => (
            <McqOption
              key={option}
              type="button"
              $state={getOptionState(optionIndex)}
              onClick={() => handleOptionClick(optionIndex)}
            >
              <McqOptionKey>{MCQ_OPTION_LABELS[optionIndex]}</McqOptionKey>
              {option}
            </McqOption>
          ))}
        </McqOptions>
        {showHint && mcq.hint && mcq.hint.heading && mcq.hint.description ? (
          <McqHint>
            <McqHintHeading>{mcq.hint.heading}</McqHintHeading>
            <McqHintText>{mcq.hint.description}</McqHintText>
          </McqHint>
        ) : null}
      </McqWrap>
      {mainsQuestion.subHeading && mainsQuestion.question ? (
        <MainsQuestion>
          <MainsLabel>{mainsQuestion.subHeading}</MainsLabel>
          <MainsText>{mainsQuestion.question}</MainsText>
        </MainsQuestion>
      ) : null}
    </>
  );
};

export default UpscTopicCardMcqMains;
